import React from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {useCommonContent} from '@/content/common'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {LocalizedNumberFormat} from '../../common/LocalizedNumberFormat'

type BrandProtectionCardContentProps = {
  labels: string[]
  title: string
  subtitle?: string
  description: string
  price?: number
  buttonText: string
  buttonVariant: ButtonVariant
  priceNote?: string
}

export const BrandProtectionCardContent: React.FC<
  React.PropsWithChildren<BrandProtectionCardContentProps>
> = ({labels, title, subtitle, description, price, buttonText, buttonVariant, priceNote}) => {
  const {t} = useTranslation()
  const {fromTitle} = useCommonContent()

  return (
    <>
      <div className="content">
        <Text variant={TextVariant.h5} element="h3" align="center" fontWeight={TextFontWeight.bold}>
          {t(title)}
        </Text>
        <div className="subtitle">
          {subtitle ? (
            <Text variant={TextVariant.h3} element="p" align="center">
              {t(subtitle)}
            </Text>
          ) : price ? (
            <>
              <Text variant={TextVariant.h3} element="p" align="center">
                {`${t(fromTitle)} `}
                <LocalizedNumberFormat value={price} type="currency" />
              </Text>
              {priceNote && (
                <div className="note">
                  <Text element="small" color={theme.palette.text.light}>
                    /{t(priceNote)}
                  </Text>
                </div>
              )}
            </>
          ) : null}
        </div>
        <ul className="labels">
          {labels.map((label) => (
            <li key={label} className="label">
              <Text
                variant={TextVariant.caption}
                fontWeight={TextFontWeight.bold}
                className="uppercase text-block"
              >
                {t(label)}
              </Text>
            </li>
          ))}
        </ul>
        <div className="description">
          <Text align="center" color={theme.palette.text.light}>
            {t(description)}
          </Text>
        </div>
      </div>
      <div className="button-wrapper">
        <Button variant={buttonVariant} type="button" fullWidth>
          {t(buttonText)}
        </Button>
      </div>

      <style jsx>{`
        .labels {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          margin-top: 12px;
        }
        .label {
          padding: ${theme.spacing[1]} ${theme.spacing[2]};
          background-color: ${theme.palette.gray[100]};
          border-radius: 4px;
        }
        .label + .label {
          margin-left: ${theme.spacing[2]};
        }
        .content {
          margin: 0 ${theme.spacing[3]};
        }
        .description {
          margin-top: ${theme.spacing[2]};
        }
        .subtitle {
          display: flex;
          justify-content: center;
          margin-top: ${theme.spacing[2]};
        }
        .note {
          margin-left: ${theme.spacing[1]};
        }
        .button-wrapper {
          margin-top: ${theme.spacing[4]};
        }
        @media ${TABLET_SCREENSIZE} {
          .content {
            margin: 0;
          }
        }
      `}</style>
    </>
  )
}
